(function () {
  const SYNC_ENDPOINT = "/apps/bmd/api/cartBackup/sync";
  const ITEMS_ENDPOINT = "/apps/bmd/api/cartBackup/items";
  const CART_ENDPOINT = (window.routes && window.routes.cart_url ? window.routes.cart_url : "/cart") + ".js";
  const CART_ADD_ENDPOINT = window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : "/cart/add";
  const CUSTOMIZATION_QUERY_PARAM = "bmd_customization";
  const CUSTOMIZATION_ADDED_QUERY_PARAM = "bmd_added";
  const SYNCING_CLASS = "bmd-cart-backup-syncing";
  const HANDOFF_LOADING_CLASS = "bmd-cart-handoff-loading";
  const STYLE_ID = "bmd-cart-backup-style";
  let syncTimer = null;
  let inFlight = null;
  let restoreInFlight = null;
  let didRestore = false;

  function ensureSyncStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      body.${SYNCING_CLASS} cart-items .cart__contents,
      body.${SYNCING_CLASS} cart-items .cart__warnings,
      body.${SYNCING_CLASS} .cart-drawer cart-drawer-items,
      body.${SYNCING_CLASS} .cart-drawer .drawer__inner-empty,
      body.${SYNCING_CLASS} .cart-drawer__footer {
        opacity: 0;
        pointer-events: none;
      }

      body.${SYNCING_CLASS} cart-items::after,
      body.${SYNCING_CLASS} .cart-drawer .drawer__inner::after {
        content: "Updating cart...";
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 180px;
        padding: 32px;
        color: rgba(var(--color-foreground), 0.75);
        font-size: 1.5rem;
      }
    `;
    document.head.appendChild(style);
  }

  function setCartSyncing(isSyncing) {
    ensureSyncStyle();
    document.body.classList.toggle(SYNCING_CLASS, !!isSyncing);
    if (!isSyncing) {
      document.documentElement.classList.remove(HANDOFF_LOADING_CLASS);
    }
  }

  function normalizeProperties(properties) {
    const normalized = {};
    if (!properties || typeof properties !== "object") return normalized;

    Object.entries(properties).forEach(([key, value]) => {
      const normalizedValue = value === null || value === undefined ? "" : String(value).trim();
      if (!normalizedValue || normalizedValue === "null" || normalizedValue === "undefined") return;
      if (key === "_backup_cart_id") return;
      normalized[key] = normalizedValue;
    });

    return normalized;
  }

  function propertySignature(properties) {
    const normalized = normalizeProperties(properties);
    return JSON.stringify(
      Object.keys(normalized)
        .sort()
        .reduce((acc, key) => {
          acc[key] = normalized[key];
          return acc;
        }, {})
    );
  }

  function lineSignature(item) {
    return [
      String(item.variant_id || item.id || ""),
      String(item.selling_plan_id || item.selling_plan_allocation?.selling_plan?.id || ""),
      propertySignature(item.properties || {}),
    ].join("|");
  }

  function normalizeItem(item) {
    return {
      key: item.key,
      id: item.id,
      variant_id: item.variant_id || item.id,
      quantity: item.quantity,
      properties: normalizeProperties(item.properties || {}),
      selling_plan_id: item.selling_plan_allocation?.selling_plan?.id || item.selling_plan_id || "",
    };
  }

  async function fetchCurrentCart() {
    const response = await fetch(CART_ENDPOINT, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.status}`);
    }
    return response.json();
  }

  async function postCartBackup(cart) {
    const response = await fetch(SYNC_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({
        items: (cart.items || []).map(normalizeItem),
      }),
    });

    if (!response.ok) {
      throw new Error(`Cart backup sync failed: ${response.status}`);
    }

    return response.json();
  }

  async function fetchBackupItems() {
    const response = await fetch(ITEMS_ENDPOINT, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(`Cart backup items fetch failed: ${response.status}`);
    }

    return response.json();
  }

  function getMissingBackupItems(cart, backupItems) {
    const currentQuantities = new Map();
    (cart.items || []).forEach((item) => {
      const signature = lineSignature(normalizeItem(item));
      currentQuantities.set(signature, (currentQuantities.get(signature) || 0) + Number(item.quantity || 0));
    });

    return (backupItems || [])
      .map((item) => {
        const properties = normalizeProperties(item.properties || {});
        const signature = lineSignature({
          variant_id: item.variant_id,
          selling_plan_id: item.selling_plan_id,
          properties,
        });
        const backupQuantity = Number(item.quantity || 0);
        const currentQuantity = currentQuantities.get(signature) || 0;
        const missingQuantity = backupQuantity - currentQuantity;

        if (!Number.isFinite(missingQuantity) || missingQuantity <= 0) return null;

        currentQuantities.set(signature, currentQuantity + missingQuantity);
        return {
          id: Number(item.variant_id),
          quantity: missingQuantity,
          selling_plan: item.selling_plan_id ? Number(item.selling_plan_id) : undefined,
          properties,
        };
      })
      .filter(Boolean);
  }

  function replaceSection(id, html, selector = ".shopify-section") {
    if (!html) return;
    const current = document.getElementById(id);
    if (!current) return;
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const replacement = parsed.querySelector(selector);
    if (replacement) current.innerHTML = replacement.innerHTML;
  }

  function refreshRenderedCartSections(response) {
    const sections = response?.sections || {};
    replaceSection("cart-icon-bubble", sections["cart-icon-bubble"]);

    const drawer = document.querySelector("cart-drawer");
    if (drawer && sections["cart-drawer"]) {
      const parsed = new DOMParser().parseFromString(sections["cart-drawer"], "text/html");
      const source = parsed.querySelector("#CartDrawer");
      const target = document.querySelector("#CartDrawer");
      if (source && target) target.innerHTML = source.innerHTML;
      drawer.classList.remove("is-empty");
    }

    if (sections["main-cart-items"]) {
      const parsed = new DOMParser().parseFromString(sections["main-cart-items"], "text/html");
      const source = parsed.querySelector("cart-items");
      const target = document.querySelector("cart-items");
      if (source && target) target.innerHTML = source.innerHTML;
    }
  }

  async function addMissingItems(items) {
    if (!items.length) return null;

    const response = await fetch(CART_ADD_ENDPOINT + ".js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({
        items,
        sections: "cart-drawer,cart-icon-bubble,main-cart-items",
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(`Cart restore add failed: ${response.status}`);
    }
    refreshRenderedCartSections(data);
    return data;
  }

  function customizationIdFromUrl() {
    return new URL(window.location.href).searchParams.get(CUSTOMIZATION_QUERY_PARAM) || "";
  }

  function customizationWasAddedFromUrl() {
    return new URL(window.location.href).searchParams.has(CUSTOMIZATION_ADDED_QUERY_PARAM);
  }

  function clearCustomizationAddedMarker() {
    const cartUrl = new URL(window.location.href);
    cartUrl.searchParams.delete(CUSTOMIZATION_ADDED_QUERY_PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      `${cartUrl.pathname}${cartUrl.search}${cartUrl.hash}`
    );
  }

  async function fetchCustomizationCartPayload(customizationId) {
    const endpoint = `/apps/bmd/api/customizationSessions/${encodeURIComponent(customizationId)}/cartPayload`;
    const response = await fetch(endpoint, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload?.result || !Array.isArray(payload?.data?.items)) {
      throw new Error(payload?.detail || "Unable to load the confirmed customization.");
    }
    return payload.data.items;
  }

  function cartContainsCustomization(cart, customizationId) {
    return (cart.items || []).some(
      (item) => item.properties?._bmd_customization_id === customizationId
    );
  }

  function showCustomizationHandoffError(error) {
    const message = error instanceof Error
      ? error.message
      : "The confirmed customization could not be added to the cart.";
    const errorContainer = document.getElementById("cart-errors");
    if (errorContainer) errorContainer.textContent = message;
    console.error("[BMD] customization cart handoff failed", error);
  }

  async function addCustomizationFromUrl() {
    const customizationId = customizationIdFromUrl();
    if (!customizationId) return false;

    setCartSyncing(true);
    try {
      let cart = await fetchCurrentCart();
      if (!cartContainsCustomization(cart, customizationId)) {
        const items = await fetchCustomizationCartPayload(customizationId);
        await addMissingItems(items);
        cart = await fetchCurrentCart();
      }
      if (!cartContainsCustomization(cart, customizationId)) {
        throw new Error("Shopify accepted the customization but did not retain it in this cart session.");
      }
      await postCartBackup(cart);

      const cartUrl = new URL(
        window.routes && window.routes.cart_url ? window.routes.cart_url : "/cart",
        window.location.origin
      );
      cartUrl.searchParams.set("bmd_added", customizationId);
      window.location.replace(cartUrl.toString());
    } catch (error) {
      showCustomizationHandoffError(error);
      setCartSyncing(false);
    }
    return true;
  }

  async function restoreCurrentCustomerCart() {
    if (restoreInFlight) return restoreInFlight;
    if (didRestore) return null;
    didRestore = true;
    setCartSyncing(true);

    restoreInFlight = Promise.all([fetchCurrentCart(), fetchBackupItems()])
      .then(async ([cart, backup]) => {
        const items = backup?.items || [];

        if (!items.length) {
          await syncCurrentCart();
          return { result: true, restored: false, reason: "No backup cart items" };
        }

        const missingItems = getMissingBackupItems(cart, items);
        if (!missingItems.length) {
          // The live Shopify cart may contain items added after the last backup.
          // Keep those items and make the backup catch up to the live cart.
          await syncCurrentCart();
          return { result: true, restored: false, reason: "Local cart already contains all backup items" };
        }

        await addMissingItems(missingItems);
        await syncCurrentCart();
        return { result: true, restored: true, restoredItems: missingItems.length };
      })
      .catch((error) => {
        console.debug("[BMD] cart backup restore skipped/failed", error);
        return null;
      })
      .finally(() => {
        restoreInFlight = null;
        setCartSyncing(false);
      });

    return restoreInFlight;
  }

  async function syncCurrentCart() {
    if (inFlight) return inFlight;

    inFlight = fetchCurrentCart()
      .then(postCartBackup)
      .catch((error) => {
        console.debug("[BMD] cart backup sync skipped/failed", error);
        return null;
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  function scheduleSync(delayMs = 250) {
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncTimer = null;
      syncCurrentCart();
    }, delayMs);
  }

  window.BMDCartBackup = {
    restoreCurrentCustomerCart,
    syncCurrentCart,
    scheduleSync,
  };

  async function initializeCart() {
    if (await addCustomizationFromUrl()) return;
    if (customizationWasAddedFromUrl()) {
      clearCustomizationAddedMarker();
      return;
    }
    await restoreCurrentCustomerCart();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeCart);
  } else {
    initializeCart();
  }
})();
